/**
 * Iter60.p43.3 — "Early Departures This Week" drill-down modal.
 *
 * Sortable table of every early-departure note recorded in the current
 * week (data provided by the tile — no extra fetch). CSV export is
 * client-side so viewer roles can still download.
 *
 * Clicking a row navigates to the attendance page filtered to that
 * specific employee / date, with `has_early_departure_note=true` so
 * the drill lands on the exact row.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Download, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import ModuleIdChip from "./ModuleIdChip";

const REASON_LABELS = {
    medical: "Medical",
    family: "Family",
    school: "School",
    other: "Other",
};

const SORT_COLUMNS = ["work_date", "employee_name", "reason_code", "excused"];

function _cmp(a, b) {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
}

function _formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return String(iso).slice(0, 10);
    }
}

function _csvEscape(v) {
    const s = v == null ? "" : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

export default function EarlyDeparturesDrillModal({
    open,
    onClose,
    data,
    loading,
}) {
    const navigate = useNavigate();
    // Default sort: newest date first.
    const [sortBy, setSortBy] = useState("work_date");
    const [sortDir, setSortDir] = useState("desc");

    const rows = data?.rows || [];

    const sorted = useMemo(() => {
        const arr = rows.slice();
        arr.sort((a, b) => {
            const av = a?.[sortBy];
            const bv = b?.[sortBy];
            const c = _cmp(av, bv);
            return sortDir === "asc" ? c : -c;
        });
        return arr;
    }, [rows, sortBy, sortDir]);

    const toggleSort = (col) => {
        if (!SORT_COLUMNS.includes(col)) return;
        if (col === sortBy) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortBy(col);
            setSortDir(col === "work_date" ? "desc" : "asc");
        }
    };

    const handleRowClick = (row) => {
        const code = (row.module_code || "").toLowerCase();
        if (!code || !row.work_date || !row.employee_id) return;
        const params = new URLSearchParams({
            date_from: row.work_date,
            date_to: row.work_date,
            has_early_departure_note: "true",
            employee_id: row.employee_id,
        });
        onClose?.();
        navigate(`/m/${code}/attendance?${params.toString()}`);
    };

    const handleExportCsv = () => {
        // Iter60.p49 Item B — CSV drops "Employee Code" and adds
        // "Module ID" (matches on-screen table + policy).
        const header = [
            "Work Date",
            "Employee Name",
            "Module ID",
            "Module",
            "Reason",
            "Excused",
            "Note",
            "Recorded At",
        ];
        const lines = [header.map(_csvEscape).join(",")];
        for (const r of sorted) {
            lines.push(
                [
                    r.work_date || "",
                    r.employee_name || "",
                    r.module_employee_id || "",
                    r.module_code || "",
                    REASON_LABELS[r.reason_code] || r.reason_code || "",
                    r.excused ? "Yes" : "No",
                    r.note || "",
                    r.recorded_at || "",
                ]
                    .map(_csvEscape)
                    .join(","),
            );
        }
        const csv = lines.join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const start = data?.range?.start || "start";
        const end = data?.range?.end || "end";
        a.download = `early-departures-${start}-to-${end}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const total = data?.total ?? sorted.length;
    const rangeLabel = data?.range?.label || "";

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent
                className="max-w-4xl"
                data-testid="early-dep-drill-modal"
            >
                <DialogHeader>
                    <DialogTitle data-testid="early-dep-drill-title">
                        Early Departures · This Week · {total} note
                        {total === 1 ? "" : "s"}
                    </DialogTitle>
                </DialogHeader>
                <div
                    className="text-xs mb-3"
                    style={{ color: "var(--asl-text-3)" }}
                    data-testid="early-dep-drill-subtitle"
                >
                    {rangeLabel}
                    {" · "}
                    Click a row to open the attendance record. Read-only
                    for viewer roles.
                </div>

                <div className="flex items-center justify-end mb-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportCsv}
                        disabled={loading || sorted.length === 0}
                        data-testid="early-dep-drill-export-csv"
                    >
                        <Download className="h-4 w-4 mr-1" aria-hidden="true" />
                        Export CSV
                    </Button>
                </div>

                {loading ? (
                    <div
                        className="asl-shimmer w-full rounded"
                        style={{ height: 280 }}
                        data-testid="early-dep-drill-loading"
                    />
                ) : sorted.length === 0 ? (
                    <div
                        className="border rounded-md text-center px-4 py-10"
                        style={{
                            color: "var(--asl-text-3)",
                            borderColor: "var(--asl-border, #E5E7EB)",
                        }}
                        data-testid="early-dep-drill-empty"
                    >
                        No early departures recorded in the current
                        week window.
                    </div>
                ) : (
                    <div
                        className="max-h-96 overflow-auto border rounded-md"
                        style={{ borderColor: "var(--asl-border, #E5E7EB)" }}
                    >
                        <table
                            className="w-full text-xs"
                            data-testid="early-dep-drill-table"
                        >
                            <thead className="asl-navy-bg text-white sticky top-0">
                                <tr>
                                    <SortableTh
                                        col="work_date"
                                        label="Date"
                                        sortBy={sortBy}
                                        sortDir={sortDir}
                                        onClick={toggleSort}
                                    />
                                    <SortableTh
                                        col="employee_name"
                                        label="Employee"
                                        sortBy={sortBy}
                                        sortDir={sortDir}
                                        onClick={toggleSort}
                                    />
                                    <th className="text-left px-3 py-2">
                                        Module
                                    </th>
                                    <SortableTh
                                        col="reason_code"
                                        label="Reason"
                                        sortBy={sortBy}
                                        sortDir={sortDir}
                                        onClick={toggleSort}
                                    />
                                    <SortableTh
                                        col="excused"
                                        label="Excused"
                                        sortBy={sortBy}
                                        sortDir={sortDir}
                                        onClick={toggleSort}
                                    />
                                    <th className="text-left px-3 py-2">
                                        Note
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((r) => (
                                    <tr
                                        key={r.attendance_id}
                                        onClick={() => handleRowClick(r)}
                                        className="border-t cursor-pointer hover:bg-black/[.03]"
                                        style={{
                                            borderColor:
                                                "var(--asl-border, #E5E7EB)",
                                        }}
                                        data-testid={`early-dep-drill-row-${r.attendance_id}`}
                                    >
                                        <td className="px-3 py-1.5 mono">
                                            {_formatDate(r.work_date)}
                                        </td>
                                        <td className="px-3 py-1.5">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {/* Iter60.p52 — Module ID as
                                                    crimson pill (was pale mono
                                                    text). Rendered inline so
                                                    the drill table stays dense. */}
                                                <ModuleIdChip
                                                    value={r.module_employee_id}
                                                    testid={`early-dep-drill-mid-${r.employee_id || r.id}`}
                                                />
                                                <div className="font-medium">
                                                    {r.employee_name || "—"}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3 py-1.5 mono">
                                            {r.module_code || "—"}
                                        </td>
                                        <td className="px-3 py-1.5">
                                            {REASON_LABELS[r.reason_code] ||
                                                r.reason_code ||
                                                "—"}
                                        </td>
                                        <td className="px-3 py-1.5">
                                            <span
                                                className="px-2 py-0.5 rounded text-[10px]"
                                                style={{
                                                    background: r.excused
                                                        ? "rgba(27, 42, 86, 0.08)"
                                                        : "rgba(168, 50, 74, 0.10)",
                                                    color: r.excused
                                                        ? "var(--asl-navy-2, #1B2A56)"
                                                        : "var(--asl-crimson, #A8324A)",
                                                }}
                                                data-testid={`early-dep-drill-excused-${r.attendance_id}`}
                                            >
                                                {r.excused ? "Yes" : "No"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-1.5">
                                            <div
                                                className="truncate"
                                                style={{ maxWidth: 260 }}
                                                title={r.note || ""}
                                            >
                                                {r.note || "—"}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onClose}
                        data-testid="early-dep-drill-close"
                    >
                        Close
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function SortableTh({ col, label, sortBy, sortDir, onClick }) {
    const active = sortBy === col;
    const Icon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
        <th
            className="text-left px-3 py-2 cursor-pointer select-none"
            onClick={() => onClick(col)}
            data-testid={`early-dep-drill-sort-${col}`}
        >
            <span className="inline-flex items-center gap-1">
                {label}
                <Icon
                    className="h-3 w-3"
                    aria-hidden="true"
                    style={{ opacity: active ? 1 : 0.5 }}
                />
            </span>
        </th>
    );
}
